import { Request, Response, NextFunction, RequestHandler } from 'express';
import { Logger } from '@unidev-hub/logger';

/**
 * Options for request logger middleware
 */
export interface RequestLoggerOptions {
  /**
   * Skip logging for specific paths
   */
  skip?: string[];

  /**
   * Log level to use
   * @default 'info'
   */
  level?: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

  /**
   * Include request body in logs
   * @default false
   */
  includeBody?: boolean;

  /**
   * Include request headers in logs
   * @default false
   */
  includeHeaders?: boolean;
}

/**
 * Middleware to log HTTP requests
 *
 * @param logger Logger instance
 * @param options Request logger options
 * @returns Express middleware
 */
export function requestLoggerMiddleware(
  logger: Logger,
  options: RequestLoggerOptions = {}
): RequestHandler {
  const skipPaths = options.skip || [];
  const logLevel = options.level || 'info';
  const includeBody = options.includeBody || false;
  const includeHeaders = options.includeHeaders || false;

  return (req: Request, res: Response, next: NextFunction) => {
    // Skip logging for specified paths
    if (skipPaths.some(path => req.path.startsWith(path))) {
      return next();
    }

    // Record start time
    const startTime = Date.now();

    // Store original end method
    const originalEnd = res.end;

    // Override end method to log response
    res.end = function(chunk?: any, encoding?: BufferEncoding | (() => void), callback?: () => void): any {
      // Calculate duration
      const duration = Date.now() - startTime;

      // Create log info
      const logInfo: Record<string, any> = {
        method: req.method,
        path: req.originalUrl || req.url,
        status: res.statusCode,
        duration: `${duration}ms`,
        ip: req.ip || req.socket.remoteAddress,
      };

      // Add correlation ID if available
      if ((req as any).correlationId) {
        logInfo.correlationId = (req as any).correlationId;
      }

      // Add request body if enabled and available
      if (includeBody && req.body) {
        logInfo.body = req.body;
      }

      // Add request headers if enabled
      if (includeHeaders) {
        logInfo.headers = req.headers;
      }

      // Log request info with the appropriate log level
      if (logLevel === 'trace') {
        logger.trace(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      } else if (logLevel === 'debug') {
        logger.debug(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      } else if (logLevel === 'info') {
        logger.info(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      } else if (logLevel === 'warn') {
        logger.warn(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      } else if (logLevel === 'error') {
        logger.error(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      } else {
        logger.info(`${req.method} ${req.originalUrl || req.url} ${res.statusCode} ${duration}ms`, logInfo);
      }

      // Handle callback being in the encoding parameter (allowed by Node.js)
      if (typeof encoding === 'function') {
        callback = encoding;
        encoding = undefined;
      }

      // Call original end method and preserve its return value
      return originalEnd.call(this, chunk, encoding as BufferEncoding, callback);
    };

    next();
  };
}
